import { ChevronDownIcon, SearchIcon } from "lucide-react";
import { useState, useMemo, useRef, useEffect } from "react";
import { useUsers } from "../../../hooks/useUsers";
import { useLocations } from "../../../hooks/useLocations";
import strings from "../../../i18n/i18nStrings";
import Modal from "../modal/Modal";
import { toast } from "react-toastify";
import { TUser } from "../../../types/Users";

const EditUserModal = ({ onClose, user, isCurrentUser = false }: { onClose: () => void; user?: TUser; isCurrentUser?: boolean }) => {
  const { updateUser, deleteUser, mutate } = useUsers();
  const { locations } = useLocations();
  const [enableNotifications, setEnableNotifications] = useState(user?.send_device_notifications || false);
  const [selectedLocations, setSelectedLocations] = useState<string[]>(user?.location_alert_selections || []);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);

  // Reset dropdown state when modal opens
  useEffect(() => {
    setIsDropdownOpen(false);
    setSearchTerm("");
    // Set initial values from user
    if (user) {
      setEnableNotifications(user.send_device_notifications || false);
      setSelectedLocations(user.location_alert_selections || []);
    }
  }, [user]);

  const filteredLocations = useMemo(() => {
    if (!locations) return [];
    return locations.filter(location => 
      location.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [locations, searchTerm]);

  const isAllSelected = locations && selectedLocations.length === locations.length;

  const handleSelectAll = () => {
    if (locations) {
      setSelectedLocations(locations.map(loc => loc._id));
    }
  };

  const handleUnselectAll = () => {
    setSelectedLocations([]);
  };

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocations(prev => 
      prev.includes(locationId) 
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const getDropdownMaxHeight = () => {
    if (!dropdownRef.current) return '12rem';
    
    const dropdown = dropdownRef.current;
    const modalContent = dropdown.closest('div[class*="max-h-"]');
    
    if (modalContent) {
      const modalRect = modalContent.getBoundingClientRect();
      const dropdownRect = dropdown.getBoundingClientRect();
      
      const availableHeight = modalRect.bottom - dropdownRect.bottom - 60;
      const maxHeight = Math.max(200, Math.min(availableHeight, 300));
      return `${maxHeight}px`;
    }
    
    return '12rem';
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-scroll to bottom when dropdown opens with custom smooth animation
  useEffect(() => {
    if (labelRef.current) {
      const modalContent = labelRef.current.closest('div[class*="overflow-y-auto"]');
      
      if (modalContent) {
        if (isDropdownOpen) {
          setTimeout(() => {
            const startTime = performance.now();
            const startScroll = modalContent.scrollTop;
            const targetScroll = modalContent.scrollHeight - modalContent.clientHeight;
            const duration = 800;
            
            const easeInOut = (t: number) => {
              return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            };
            
            const animate = (currentTime: number) => {
              const elapsed = currentTime - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const easedProgress = easeInOut(progress);
              
              modalContent.scrollTop = startScroll + (targetScroll - startScroll) * easedProgress;
              
              if (progress < 1) {
                requestAnimationFrame(animate);
              }
            };
            
            requestAnimationFrame(animate);
          }, 150);
        } else {
          modalContent.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [isDropdownOpen]);

  const handleSave = async () => {
    if (!user) return;
    
    try {
      const updatedData = {
        send_device_notifications: enableNotifications,
        location_based_alerts: enableNotifications && selectedLocations.length > 0,
        location_alert_selections: enableNotifications ? selectedLocations : []
      };
      
      await updateUser(user._id, updatedData);
      await mutate();
      onClose();
      toast.success(`${user.first_name}'s settings have been updated`);
    } catch (error) {
      toast.error("Failed to update user settings");
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    
    try {
      await deleteUser(user._id);
      await mutate();
      onClose();
      toast.success(`${user.first_name} has been deleted`);
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  if (!user) return null;

  return (
    <Modal title={`Edit ${user.first_name}`} onClose={onClose} expanded={isDropdownOpen}>
      <div className="space-y-6">
        {/* User Info */}
        <div className="bg-gray-50 p-4 rounded-md">
          {/* <h3 className="text-sm font-medium text-gray-900 mb-2">User Information</h3> */}
          <p className="text-sm text-gray-600 pb-3">
            <strong>Name:</strong> {user.first_name} {user.last_name}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Email:</strong> {user.email_address}
          </p>
        </div>

        {/* Notification Settings */}
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="enableNotifications"
              checked={enableNotifications}
              onChange={(e) => {
                setEnableNotifications(e.target.checked);
                if (!e.target.checked) {
                  setSelectedLocations([]);
                } else {
                  setSelectedLocations(locations?.map(loc => loc._id) || []);
                }
              }}
              className="h-4 w-4 mt-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="enableNotifications"
              className="ml-2 text-xs font-bold text-gray-700 uppercase tracking-wider"
            >
              Enable Device Notifications
            </label>
          </div>
          <p className="text-sm font-medium text-gray-900 mb-4">Get email updates for important device notifications and alerts.</p>
          
          <div className="ml-0 mt-6">
            <label ref={labelRef} className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
              Get Notification for...
            </label>
            
            {locations && locations.length > 0 && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  disabled={!enableNotifications}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`mt-1 w-full border rounded-md shadow-sm py-2 px-3 text-left focus:outline-none sm:text-sm flex items-center justify-between ${
                    !enableNotifications 
                      ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed" 
                      : isDropdownOpen
                      ? "border-blue-500 ring-1 ring-blue-500 bg-white"
                      : selectedLocations.length > 0
                      ? "border-blue-300 bg-blue-50 hover:bg-blue-100"
                      : "border-gray-300 bg-white hover:bg-gray-50"
                  }`}
                >
                  <span className="text-sm">
                    {selectedLocations.length === 0 
                      ? "Select locations..." 
                      : isAllSelected
                      ? `All Locations (${locations.length})`
                      : `${selectedLocations.length} location${selectedLocations.length === 1 ? '' : 's'} selected`
                    }
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
              
              {isDropdownOpen && enableNotifications && (
                <div 
                  className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden flex flex-col"
                  style={{ 
                    maxHeight: getDropdownMaxHeight(),
                    top: '100%',
                    marginTop: '4px'
                  }}
                >
                  {/* Search */}
                  <div className="p-2 border-b border-gray-200 flex-shrink-0">
                    <div className="relative">
                      <SearchIcon className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search locations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  {/* Scrollable content including All Locations and location list */}
                  <div className="flex-1 overflow-y-auto">
                    {/* All/Unselect All */}
                    <div className="p-2 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={isAllSelected ? handleUnselectAll : handleSelectAll}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">All Locations</span>
                        </label>
                        <button
                          type="button"
                          onClick={handleUnselectAll}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Unselect All
                        </button>
                      </div>
                    </div>
                    
                    {/* Location Options */}
                    <div>
                      {filteredLocations.map((location) => (
                        <label
                          key={location._id}
                          className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLocations.includes(location._id)}
                            onChange={() => handleLocationToggle(location._id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{location.name}</span>
                        </label>
                      ))}
                      {filteredLocations.length === 0 && (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No locations found
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              </div>
            )}
            
            {(!locations || locations.length === 0) && enableNotifications && (
              <div className="mt-1 p-3 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-md">
                No locations available for notifications.
              </div>
            )}
          </div>
        </div>
        
        {/* Add space when dropdown is open to push buttons below dropdown */}
        {isDropdownOpen && <div className="h-60"></div>}
        
        {/* Delete User Section - only show for other users */}
        {!isCurrentUser && (
          <div className="border-t pt-6">
            {/* <h3 className="text-sm font-medium text-red-900 mb-2">Danger Zone</h3> */}
            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-1 py-2 text-sm font-semibold underline text-red-700 hover:text-red-800"
              >
                Delete User
              </button>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800 mb-3">
                  Are you sure you want to delete <strong>{user.first_name}</strong>? This action cannot be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    className="px-3 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md"
                  >
                    Yes, Delete User
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-2 text-sm text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-3 border border-transparent rounded-md shadow-sm text-xs font-bold uppercase tracking-wider text-white bg-blue-700 hover:bg-blue-800"
          >
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditUserModal;